import { QUERY_STRING_KEY } from '../@deprecation/constants/app';
import {
  getLocalStorage,
  getSessionStorage,
  getTenantId,
  isCollectionPage,
  isProductPage,
  isSearchPage,
  setLocalStorage,
  setSessionStorage,
} from '../@deprecation/utils';
import {
  getAttentiveInfo,
  initReceiveEventAttentive,
  isAttentiveActive,
} from '../@deprecation/utils/attentive';
import {
  getGa4Info,
  getGa4PageLocation,
  isGa4IntegrationActive,
} from '../@deprecation/utils/ga4-integration';
import { isKlaviyoActive } from '../@deprecation/utils/integration';
import { KEY_VOLUME_BUNDLE_DATA_SESSION } from '../@deprecation/volume-bundle/constants';
import { KEY_DYNAMIC_BUNDLE_DATA_SESSION } from '../modules/predictive-bundle/predictive-bundle.legacy';
import { startSession } from '../workers/workers-provider';
import {
  ACTION,
  ANALYTICS_URL,
  ATTENTIVE_BOOST_EVENT,
  BOOST_CART_TOKEN,
  BOOST_SD_PRODUCT_IDS_CLICKED,
  DEFAULT_SELECTOR,
  DYNAMIC_BUNDLE_TYPE,
  MAP_PATH_CLICK,
  PLACEMENT,
  PopupBundlePlacement,
  PRE_ACTION,
  prefix,
  RECOMMEND_ACTIVE,
  REQUEST_ID_PRE_ORDER_VIEW,
  SHOPIFY_CART_TOKEN,
  TRACKING_BUNDLE_TYPE,
  USER_ACTION,
} from './constants';
import {
  initGa4WidgetViewTracking,
  sendGa4AddToCartEvent,
  sendGa4FilterOptionValueClickEvent,
  sendGa4NotifyMeEvent,
  sendGa4SearchQueryEvent,
  sendGa4SearchResultIswClickEvent,
  sendGa4SuggestionTermClickEvent,
  sendGa4SuggestionTermIswClickEvent,
} from './ga4-tracking';
import {
  addEventKeyPressForSearching,
  applyCombinedListingOverride,
  buildDataDynamicBundle,
  buildDataVolumeBundle,
  buildGeneralData,
  checkActiveElementWhiteList,
  checkSendEventKlaviyoViewCollection,
  checkSendEventWhenLoadPage,
  createBoostTokenUpdateCartAttribute,
  generateRIDPreOrder,
  generateUUID,
  getCustomerId,
  getExtraData,
  getModalQuickView,
  getParamsHistory,
  getPlacementBundle,
  getProductIdFromQuickView,
  handleFilterOptionItemButtonClick,
  handleKlaviyo,
  identifyUser,
  isClickAddToCart,
  isExcludeAddToCart,
  postData,
  prefetchCombinedListingData,
  saveSessionStorageClicked,
  sendKlaviyoEventChangeRangeSlider,
  sendKlaviyoEventInCollectionSearch,
  setProductExtraInfo,
} from './main-thread';

window.boostAnalytic = DEFAULT_SELECTOR;

/**
 * @type {import('./index.d').sendEvent}
 */
function sendEvent(data, _activeElement) {
  const runtime = window.boostWidgetIntegrationConfig?.analytics?.runtime || 'worker'; // "worker" or "main-thread"

  // Prevent sent event data when view product have not productId - case bundle as product
  if (!data.pid && data.u === USER_ACTION.VIEW_PRODUCT) {
    return;
  }

  if (data.a === ACTION.RECOMMEND) {
    // not send qs when action recommend exclude bundle
    if (!data.actionBundle) {
      data.qs = null;
    }
  }
  if (data.a === ACTION.RECOMMEND) {
    const recommendActive = getLocalStorage(RECOMMEND_ACTIVE) || {};
    data = {
      ...data,
      ...recommendActive,
    };
  }

  setLocalStorage(PRE_ACTION, data.a);

  // prevent all event of widget which widget not come from turbo
  if (!data.rid) return;

  // buy now it will reset attribute cart but not reset cart_token so pass cart_token have been generated when call updated cart
  // cart_token only change when it be done payment.
  if (USER_ACTION.BUY_NOW === data.u) {
    data.ct = getLocalStorage(SHOPIFY_CART_TOKEN);
  }

  // Table 5: boost_bundle_interacted - add discount_applied field
  if (data.actionBundle && data.wid) {
    const isVolume = data.rt === TRACKING_BUNDLE_TYPE.VOLUME_BUNDLE;
    const bundleSession =
      getSessionStorage(
        isVolume ? KEY_VOLUME_BUNDLE_DATA_SESSION : KEY_DYNAMIC_BUNDLE_DATA_SESSION
      ) || {};
    const bundleData = bundleSession[data.wid];
    const av = bundleData?.amountValueDisplay;
    // volume bundle stores discountApplied boolean; dynamic bundle stores amountValueDisplay string
    data.da = isVolume ? !!bundleData?.discountApplied : !!av;
  }

  if (runtime === 'worker' && data.u !== USER_ACTION.BUY_NOW) {
    try {
      startSession('analytics').send({ action: 'sendEvent', data, extraData: getExtraData() });
    } catch (error) {
      console.log('error sendEvent', error);
    }
  } else {
    // new approach have generated cart_token so don't need refresh cart token before sendEvent
    postData(ANALYTICS_URL, data);
  }

  // Table 6: boost_preorder_placed - send granular event when user places a preorder
  if (data.u === USER_ACTION.ATC_PRE_ORDER) {
    const campaignId =
      data.campaign_id ||
      (() => {
        try {
          const preOrderVariants = window.boostSdPreOrderData?.variants || [];
          const variant = preOrderVariants.find((v) => String(v.id) === String(data.vid));
          return variant?.preorder?.settings?.campaignId || '';
        } catch {
          return '';
        }
      })();

    const dataPostHog = {
      tenant_id: data.tid,
      clicked_at: data.t,
      type: 'boost_preorder_placed',
      data: {
        product_id: String(data.pid || ''),
        variant_id: String(data.vid || ''),
        campaign_id: campaignId,
        market: window.Shopify?.country || '',
        rid: data.rid,
        eid: data.eid,
        cid: data.cid || '',
      },
    };

    fetch(ANALYTICS_URL, {
      method: 'POST',
      body: JSON.stringify(dataPostHog),
    }).catch(() => {});
  }
}

/**
 * @type {import('./index.d').checkViewPreOrder}
 */
function checkViewPreOrder(pid = 0) {
  let productId = pid;
  if (isProductPage()) {
    productId = window.boostWidgetIntegration?.generalSettings?.product_id || window.__st?.rid;
  }

  if (!productId) return;

  let attempts = 0;
  const maxAttempts = 100;

  const intervalId = setInterval(() => {
    // find element have class boost-sd__preorder-title
    const hasPreOrderBlock = document.querySelector(
      '.boost-sd__preorder-option .boost-sd__preorder-title'
    );

    if (hasPreOrderBlock) {
      clearInterval(intervalId);
      const data = buildGeneralData({ u: USER_ACTION.VIEW_PRODUCT_PRE_ORDER, pid: productId });

      const clicked = getSessionStorage(BOOST_SD_PRODUCT_IDS_CLICKED) || {};
      if (clicked[productId] && clicked[productId] !== 'none') {
        data.rid = clicked[productId];
        data.a = clicked[`${productId}_action`];
      } else {
        data.rid = generateRIDPreOrder();
        setSessionStorage(REQUEST_ID_PRE_ORDER_VIEW, data.rid);
        data.a = 'other';
      }

      sendEvent(data);
    }

    // If not found and re-try maxAttempts -> stop
    attempts++;
    if (attempts >= maxAttempts) {
      clearInterval(intervalId);
    }
  }, 100);
}

/**
 * @type {import('./index.d').checkAndUpdateBoostCartToken}
 */
function checkAndUpdateBoostCartToken() {
  fetch('/cart.js')
    .then((res) => res.json())
    .then((cart) => {
      // BOOST_CART_TOKEN.SHORT_KEY: boost_cart_token
      if (!cart?.attributes?.[BOOST_CART_TOKEN.SHORT_KEY]) {
        createBoostTokenUpdateCartAttribute();
      }

      if (cart?.attributes?.[BOOST_CART_TOKEN.SHORT_KEY]) {
        // Update localStorage with the boost cart token
        setLocalStorage(BOOST_CART_TOKEN.STORAGE_KEY, cart.attributes[BOOST_CART_TOKEN.SHORT_KEY]);
      }
    });
}

/**
 * Table 7: boost_back_in_stock_subscribed
 * Sends a granular analytics event when a user subscribes to back-in-stock notifications.
 */
function handleBackInStockSubscribedEvent(event) {
  const { productId, variantId, notificationChannel, klaviyoSynced } = event.detail;

  const dataPostHog = {
    tenant_id: getTenantId(),
    clicked_at: new Date().toISOString(),
    type: 'boost_back_in_stock_subscribed',
    data: {
      product_id: String(productId || ''),
      variant_id: String(variantId || ''),
      notification_channel: notificationChannel || 'email',
      klaviyo_synced: !!klaviyoSynced,
      eid: generateUUID(),
      rid: generateUUID(),
      cid: getCustomerId() || '',
    },
  };

  fetch(ANALYTICS_URL, {
    method: 'POST',
    body: JSON.stringify(dataPostHog),
  }).catch(() => {});

  // GA4: boost_notify_me
  sendGa4NotifyMeEvent({
    productId: String(productId || ''),
    searchQuery: new URLSearchParams(window.location.search).get('q') || '',
  });
}

/**
 * @type {import('./index.d').initAnalytic}
 */
export function initAnalytic() {
  if (isSearchPage()) {
    setLocalStorage(QUERY_STRING_KEY, getParamsHistory());
  }

  // For Klaviyo
  identifyUser();
  checkSendEventKlaviyoViewCollection();
  addEventKeyPressForSearching();
  // End for Klaviyo

  // For Attentive
  initReceiveEventAttentive();
  // End for Attentive

  window.addEventListener('debounce-in-collection-search', sendKlaviyoEventInCollectionSearch);
  window.addEventListener('change-range-slider', sendKlaviyoEventChangeRangeSlider);
  window.addEventListener('re-view-collection', checkSendEventKlaviyoViewCollection);
  window.addEventListener('boost-sd-bis-subscribed', handleBackInStockSubscribedEvent);

  window.addEventListener('debounce-in-collection-search', (e) => {
    if (e.detail?.searchTerm) {
      sendGa4SearchQueryEvent({
        searchQuery: e.detail.searchTerm,
      });
    }
  });

  // GA4: unified click handler — covers all GA4 click-based events so we only
  // pay the cost of one capture-phase listener. Each condition is independent;
  // multiple may fire for a single click (e.g. a suggestion link also matches
  // the search-result-ISW branch), so we do NOT use early returns between them.
  window.addEventListener(
    'click',
    (event) => {
      const target = event.target;

      // --- boost_search_query: "View all results" button in ISW ---
      if (target.closest('[data-group="view-all-products"]')) {
        const input =
          document.querySelector('.boost-sd__search-bar-input') ||
          document.querySelector('.boost-sd__search-widget-init-input');
        const query = input?.value?.trim() || '';
        if (query) sendGa4SearchQueryEvent({ searchQuery: query });
        // Intentional return: view-all-products should not trigger other GA4 events.
        return;
      }

      // --- boost_search_query: search-page form submit button ---
      // Reads query from .boost-sd__search-form-input inside the same <form>.
      if (target.closest('.boost-sd__search-form-submit-btn')) {
        const form = target.closest('form.boost-sd__search-form');
        const formInput = form?.querySelector('.boost-sd__search-form-input');
        const query = formInput?.value?.trim() || '';
        if (query) sendGa4SearchQueryEvent({ searchQuery: query });
        return;
      }

      // --- boost_search_query: submit button inside ISW container ---
      const iswSubmitBtn = target.closest(
        '.boost-sd__instant-search-container [type="submit"],' +
          ' .boost-sd__search-widget-init-wrapper [type="submit"]'
      );
      if (iswSubmitBtn) {
        const form = iswSubmitBtn.closest('form');
        const formInput = form?.querySelector(
          '.boost-sd__search-bar-input, .boost-sd__search-widget-init-input, [name="q"]'
        );
        const query = formInput?.value?.trim() || '';
        if (query) sendGa4SearchQueryEvent({ searchQuery: query });
        return;
      }

      // --- boost_suggestion_term_click: spelling-correction suggestion link ---
      const spellingLink = target.closest('a.boost-sd__spelling-correction-suggest-link');
      if (spellingLink) {
        const suggestionTerm = spellingLink.textContent?.trim() || '';
        const container = spellingLink.closest('.boost-sd__spelling-correction');
        const searchQuery =
          container?.dataset?.label || new URLSearchParams(window.location.search).get('q') || '';
        sendGa4SuggestionTermClickEvent({ suggestionTerm, searchQuery });
        return;
      }

      // --- ISW suggestion-link shared detection ---
      // Both boost_suggestion_term_isw_click and boost_search_result_isw_click
      // share the same anchor selectors, so we resolve them in one branch.
      const iswLink = target.closest(
        'a.boost-sd__suggestion-queries-link, a.boost-sd__suggestion-queries-item-link'
      );
      if (iswLink) {
        const href = iswLink.href || '';
        const searchInput = document.querySelector('.boost-sd__search-widget-init-input');
        const searchQuery = searchInput?.value?.trim() || '';

        // boost_suggestion_term_isw_click — suggestion terms always navigate to /search?q=...
        // Different ISW layouts use different CSS modifiers (--suggestion, --most, etc.)
        // so href is the reliable discriminator. "View all results" is excluded above.
        if (
          iswLink.matches('a.boost-sd__suggestion-queries-link') &&
          href.includes('/search?') &&
          !iswLink.closest('[data-group="view-all-products"]')
        ) {
          const suggestionTerm = iswLink.textContent?.trim() || '';
          sendGa4SuggestionTermIswClickEvent({ suggestionTerm, searchQuery });
          return;
        }

        // boost_search_result_isw_click — collection, page, or blog results in ISW.
        // result_type is derived from the href path.
        // Suggestion term links (/search?q=...) are handled above so no double-firing.
        let resultType = 'other';
        if (href.includes('/collections/')) resultType = 'collection';
        else if (href.includes('/blogs/') || href.includes('/articles/')) resultType = 'blog';
        else if (href.includes('/pages/')) resultType = 'page';
        else return; // /search?q=... and unrecognised hrefs — skip
        sendGa4SearchResultIswClickEvent({ resultType, searchQuery });
      }
    },
    true
  );

  window.addEventListener(
    'keydown',
    (event) => {
      if (event.key !== 'Enter') return;
      const input = event.target.closest(
        '.boost-sd__search-widget-init-input, .boost-sd__search-bar-input, .boost-sd__search-form-input'
      );
      if (!input) return;
      // Check whether a keyboard-navigated item is currently selected
      const selected = document.querySelector('.boost-sd__suggestion-queries-item-selected');
      // If a suggestion/result item is selected (not the View-All button), skip —
      // the controller will navigate to that item; boost_suggestion_term_isw_click
      // or boost_search_result_isw_click handles those cases.
      if (selected && selected.dataset?.group !== 'view-all-products') return;
      const query = input.value?.trim() || '';
      if (query) sendGa4SearchQueryEvent({ searchQuery: query });
    },
    true
  );

  // GA4: boost_widget_view — observe recommendation/bundle widgets entering
  // the viewport via IntersectionObserver + MutationObserver.
  initGa4WidgetViewTracking();

  // GA4: boost_add_to_cart — RCM cart-drawer recommendation
  // The RCU controller stops propagation, so we use a custom event to keep
  // GA4 logic in the analytic module.
  window.addEventListener('boost-sd-rcu-add-to-cart', (e) => {
    const { productId, variantId, widgetId } = e.detail || {};
    if (productId) {
      sendGa4AddToCartEvent({ productId, variantId, widgetId, source: 'rcu' });
    }
  });

  // GA4: boost_add_to_cart — Tiered bundle (search page & product page)
  window.addEventListener('boost-sd-tier-discount-add-to-cart', (e) => {
    const { productId, variantId, widgetId } = e.detail || {};
    if (productId) {
      sendGa4AddToCartEvent({ productId, variantId, widgetId, source: 'tier-discount' });
    }
  });

  checkSendEventWhenLoadPage();
  console.log('Analytic register events - Version SSR');
  document.addEventListener('click', onClickTrackingEvent, true);

  checkAndUpdateBoostCartToken();

  checkViewPreOrder();

  // Prefetch combined listing data on PDP for analytics tracking
  prefetchCombinedListingData();
}

/**
 * @type {import('./index.d').onClickTrackingEvent}
 */
function onClickTrackingEvent(event) {
  if (!event || !event.target) return;
  let recommendData = {};

  const activeElement = event.target;
  let action = isSearchPage() ? ACTION.SEARCH : isCollectionPage() ? ACTION.FILTER : '';
  let productId = '';
  let actionBundle = false;
  let placementBundle = '';

  if (isProductPage()) {
    action = getLocalStorage(PRE_ACTION) || ACTION.FILTER;
    productId = window.boostWidgetIntegration?.generalSettings?.product_id || '';
    placementBundle = PLACEMENT.product_page;
  }

  if (isSearchPage()) {
    placementBundle = PLACEMENT.search_page;
  }

  if (activeElement.closest(window.boostAnalytic.suggest || DEFAULT_SELECTOR.suggest)) {
    action = ACTION.SUGGEST;
    placementBundle = PLACEMENT.instant_search;
  }

  if (activeElement.closest(window.boostAnalytic.recommend || DEFAULT_SELECTOR.recommend)) {
    action = ACTION.RECOMMEND;
    const recommendationElement = activeElement.closest(
      window.boostAnalytic.recommend || DEFAULT_SELECTOR.recommend
    );

    if (recommendationElement && recommendationElement.id) {
      recommendData = getLocalStorage(`boostSdRecommend-${recommendationElement.id}`) || {};

      // data recommendation dynamic bundle
      if (activeElement.closest(`.${prefix}recommendation--dynamic`)) {
        actionBundle = true;
        const dataDynamicBundle = buildDataDynamicBundle(recommendationElement);
        recommendData = {
          ...recommendData,
          ...dataDynamicBundle,
          placement: getPlacementBundle(placementBundle),
        };

        const productOfBundle = activeElement.closest(`.${prefix}recommendation-bundle-image-item`);
        // current: only click image or title then count click product bundle
        const checkWhiteListCountAsProductBundle = checkActiveElementWhiteList(activeElement, [
          `.boost-sd__recommendation-bundle-item-title`,
          `.boost-sd__recommendation-bundle-link-image`,
        ]);
        if (
          productOfBundle &&
          productOfBundle?.dataset?.productId &&
          checkWhiteListCountAsProductBundle
        ) {
          productId = productOfBundle?.dataset?.productId;
        }
      }

      // data recommendation volume bundle (inside recommendation wrapper)
      if (
        activeElement.closest(window.boostAnalytic.volumeBundle || DEFAULT_SELECTOR.volumeBundle)
      ) {
        actionBundle = true;
        const volumeBundleData = buildDataVolumeBundle(recommendationElement, placementBundle);
        recommendData = {
          ...recommendData,
          ...volumeBundleData,
        };

        const volumeBundleProductId = volumeBundleData?.pid;
        if (volumeBundleProductId) {
          productId = volumeBundleProductId;
          saveSessionStorageClicked(volumeBundleProductId, 'none'); // api require at least productId clicked
        }
      }

      setLocalStorage(RECOMMEND_ACTIVE, recommendData);
    }
  }

  const recommendationActive = getLocalStorage(RECOMMEND_ACTIVE);
  const preAction = getLocalStorage(PRE_ACTION);
  if (preAction === ACTION.RECOMMEND && recommendationActive.rt === DYNAMIC_BUNDLE_TYPE) {
    actionBundle = true;
    recommendData = recommendationActive;
  }

  const data = { a: action, pid: productId, actionBundle };

  // listener event from quick view
  window.addEventListener('boost-sd-open-quick-view', function bindDataActionToQuickView(e) {
    const quickViewModal = getModalQuickView();

    quickViewModal?.setAttribute('data-action-analytic', data?.a);
    if (e?.detail?.pid) quickViewModal?.setAttribute('data-product-id', e?.detail?.pid);

    window.removeEventListener('boost-sd-open-quick-view', bindDataActionToQuickView);

    checkViewPreOrder(e?.detail?.pid);
  });

  let isSent = false;

  if (
    !isExcludeAddToCart(activeElement) &&
    (activeElement.closest(window.boostAnalytic.addToCart || DEFAULT_SELECTOR.addToCart) ||
      isClickAddToCart(activeElement))
  ) {
    isSent = true;

    // if exist productId from quick view then re-assign pid and reset vid.
    const productIdQuickView = getProductIdFromQuickView(activeElement);
    if (productIdQuickView) {
      data.pid = productIdQuickView;
      data.vid = '';
    }

    // Combined listing: use child product ID from session storage if available
    applyCombinedListingOverride(data, activeElement);

    data.u = USER_ACTION.ADD_TO_CART;
    const actionFromQuickView = getModalQuickView()?.dataset.actionAnalytic;
    if (actionFromQuickView) {
      data.a = actionFromQuickView;
    }
  } else if (activeElement.closest(window.boostAnalytic.buyNow || DEFAULT_SELECTOR.buyNow)) {
    isSent = true;

    // if exist productId from quick view then re-assign pid and reset vid.
    const productIdQuickView = getProductIdFromQuickView(activeElement);
    if (productIdQuickView) {
      data.pid = productIdQuickView;
      data.vid = '';
    }

    // Combined listing: use child product ID from session storage if available
    applyCombinedListingOverride(data, activeElement);

    const actionFromQuickView = getModalQuickView()?.dataset.actionAnalytic;
    if (actionFromQuickView) {
      data.a = actionFromQuickView;
    }

    data.u = USER_ACTION.BUY_NOW;
    const buyNowButton = activeElement.closest(
      window.boostAnalytic.buyNow || DEFAULT_SELECTOR.buyNow
    );

    // if classList buyNowButton contains ourBuyNow then it is click from our buy it now app
    if (
      buyNowButton?.classList.contains(window.boostAnalytic.ourBuyNow || DEFAULT_SELECTOR.ourBuyNow)
    ) {
      data.bct = getLocalStorage(BOOST_CART_TOKEN.STORAGE_KEY);
    }
  } else if (activeElement.closest(window.boostAnalytic.quickView || DEFAULT_SELECTOR.quickView)) {
    isSent = true;

    data.u = USER_ACTION.QUICK_VIEW;
  } else if (activeElement.closest(window.boostAnalytic.recommend || DEFAULT_SELECTOR.recommend)) {
    isSent = true;
  }

  // if is pre-order then priority sent pre-order than view.
  if (activeElement.closest(window.boostAnalytic.preOrder || DEFAULT_SELECTOR.preOrder)) {
    isSent = true;
    if (!data.pid) {
      data.pid = getProductIdFromQuickView(activeElement);
    }

    // Combined listing: use child product ID from session storage if available
    applyCombinedListingOverride(data, activeElement);

    data.u = USER_ACTION.ATC_PRE_ORDER;
    const actionFromQuickView = getModalQuickView()?.dataset.actionAnalytic;
    if (actionFromQuickView) {
      data.a = actionFromQuickView;
    }
  }

  // Place Latest Click into Product Item - assign productId
  if (activeElement.closest(window.boostAnalytic.productItem || DEFAULT_SELECTOR.productItem)) {
    isSent = true;
    const productItem = activeElement.closest(
      window.boostAnalytic.productItem || DEFAULT_SELECTOR.productItem
    );
    const productId = productItem?.dataset?.id || productItem?.getAttribute('id');
    const variantId = productItem?.dataset?.productId; // product-id will is variant-id in turbo version

    data.pid = productId;
    data.vid = variantId || ''; // add variant_id for turbo version

    saveSessionStorageClicked(productId, 'none');
  }

  // if product page get productId from general Settings
  if (!data.pid && window.boostSDData?.product?.id) {
    data.pid = window.boostSDData?.product?.id;
  }

  const isClickPopupBundle =
    activeElement.closest('.boost-sd__recommendation-dynamic-show-popup') ||
    activeElement.closest('.boost-sd__product-item--embedded-bundle') ||
    activeElement.closest('.boost-sd__suggestion-queries-item--embedded-bundle');

  if (isClickPopupBundle) {
    actionBundle = true;
  }

  if (actionBundle) {
    // save last placement of bundle when open popup
    if ((action = ACTION.SUGGEST)) {
      const _placementPopup = PLACEMENT.instant_search;
      setSessionStorage(PopupBundlePlacement, _placementPopup);
    }

    data.placementBundle = getPlacementBundle(placementBundle);

    if (
      data.u === USER_ACTION.ADD_TO_CART &&
      activeElement.closest('.boost-sd__recommendation-bundle-button')
    ) {
      delete data.pid;
    }
  }

  handleFilterOptionItemButtonClick(activeElement);

  // GA4: boost_filter_option_value_click
  const filterOptionBtn = activeElement.closest(
    window.boostAnalytic.filterOption || DEFAULT_SELECTOR.filterOption
  );
  if (filterOptionBtn) {
    sendGa4FilterOptionValueClickEvent({
      searchQuery: new URLSearchParams(window.location.search).get('q') || '',
      filterOptionValue: filterOptionBtn.textContent?.trim() || '',
    });
  }

  // not build data, sent if isSent = false
  if (!isSent) return;

  if (isSent) {
    const buildData = buildGeneralData(data, { ...recommendData, actionBundle });
    if (data.bct) {
      buildData.bct = data.bct;
    }

    const clonedBuildData = { ...buildData };
    if (clonedBuildData.u === USER_ACTION.ATC_PRE_ORDER || isAttentiveActive()) {
      setProductExtraInfo(activeElement, clonedBuildData);
    }
    if (isAttentiveActive()) {
      const attentiveInfo = getAttentiveInfo(clonedBuildData);
      if (attentiveInfo.event_name === ATTENTIVE_BOOST_EVENT.ADD_TO_CART) {
        clonedBuildData.add_to_cart_context =
          clonedBuildData.a === ACTION.RECOMMEND ? clonedBuildData.wid : clonedBuildData.pg;
      }
      if (attentiveInfo.event_name === ATTENTIVE_BOOST_EVENT.CLICK_SEARCH_RESULT) {
        clonedBuildData.path_click = MAP_PATH_CLICK[clonedBuildData.a] || '';
      }
      clonedBuildData.attentive_info = attentiveInfo;
    }
    if (isGa4IntegrationActive()) {
      const ga4Info = getGa4Info(clonedBuildData);
      if (ga4Info.event_name) {
        clonedBuildData.page_location = getGa4PageLocation();
        clonedBuildData.ga4_info = ga4Info;
        if (new URLSearchParams(window.location.search).get('boost_ga4_debug') === '1') {
          console.log(
            `[Boost GA4] → Lambda (click) ${ga4Info.event_name}`,
            JSON.parse(JSON.stringify(clonedBuildData))
          );
        }
      }
    }
    sendEvent(clonedBuildData, activeElement);

    if (window.klaviyo && isKlaviyoActive()) {
      handleKlaviyo(activeElement, buildData);
    }
  }
}
